import { useEffect, useRef, useCallback } from 'react';
import { toast } from 'sonner';

// Routes where an active practice session lives. We never surface the update
// prompt here so it can't distract mid-recording — the check re-runs on the next
// tab focus or when the user navigates back to a safe page after the session.
const PRACTICE_ROUTES = ['/manual', '/practice', '/automatic', '/listen'];

// Public pages, where the prompt is noise rather than help. A visitor reading
// the landing page or the legal pages has no session to protect and no reason
// to care which build they are on; the prompt only reads as something being
// wrong with the site. Signed-in users still get it everywhere else in the app.
const PUBLIC_ROUTES = ['/', '/reset-password', '/unsubscribe'];
// Blog pages too. Posts sit at the root (/why-i-built-mimique), so after the
// app's own first-level paths, any other single segment is a post.
const APP_SEGMENTS = [
    'dashboard', 'collections', 'settings', 'manual', 'practice', 'automatic',
    'listen', 'onboarding', 'changelog', 'help', 'admin',
];
const isBlogPath = (p) => {
    const path = String(p);
    if (path === '/blog' || path.startsWith('/blog/') || path === '/install') return true;
    const parts = path.split('/').filter(Boolean);
    return parts.length === 1 && !APP_SEGMENTS.includes(parts[0]) && !PUBLIC_ROUTES.includes('/' + parts[0]);
};
// The admin pages are not the app either: the prompt is for customers.
const isAdminPath = (p) => String(p) === '/admin' || String(p).startsWith('/admin/');
const isPublicPath = (p) => PUBLIC_ROUTES.includes(p) || String(p).startsWith('/legal/') || isBlogPath(p) || isAdminPath(p);

// Extract the hashed bundle filename (e.g. "main.42ff0cdd.js") from a path/URL.
const bundleName = (p) => {
    const m = p && String(p).match(/main\.[A-Za-z0-9]+\.js/);
    return m ? m[0] : null;
};

// The bundle this tab is ACTUALLY running, read from the live <script> tag —
// ground truth for the version currently executing.
const runningBundle = () => {
    const s = document.querySelector('script[src*="/static/js/main."]');
    return s ? bundleName(s.src) : null;
};

// The bundle the server is currently serving. asset-manifest.json is revalidated
// on every request (Cache-Control: max-age=0, must-revalidate), so no-store here
// always reflects the latest deploy.
const latestBundle = async () => {
    const res = await fetch('/asset-manifest.json', { cache: 'no-store' });
    if (!res.ok) throw new Error('manifest ' + res.status);
    const json = await res.json();
    return bundleName(json.files && json.files['main.js']);
};

// Detects when a newer build has been deployed while this tab stayed open, and
// offers a one-tap reload. Single-page tabs otherwise run stale code for days:
// although index.html revalidates, an already-open tab never re-fetches it, and
// the service worker registered in index.js only picks up a new build on a
// fresh load. (This has bitten iOS and Chrome testers — see
// MIMIQUE-STATUS follow-ups.) The prompt only ever offers a reload; it never
// reloads on its own, so an in-progress recording is never interrupted.
export const useVersionCheck = (pathname) => {
    const bootRef = useRef(runningBundle());
    const promptedRef = useRef(false);
    const pathRef = useRef(pathname);
    pathRef.current = pathname;

    const check = useCallback(async () => {
        if (promptedRef.current || document.visibilityState !== 'visible') return;
        if (PRACTICE_ROUTES.includes(pathRef.current)) return; // don't distract mid-session
        if (isPublicPath(pathRef.current)) return; // app only, not the public pages
        if (!bootRef.current) return; // couldn't read the running bundle — skip rather than false-prompt
        let latest;
        try { latest = await latestBundle(); } catch { return; }
        if (!latest || latest === bootRef.current) return;
        promptedRef.current = true;
        toast('A new version of Mimique is available.', {
            id: 'app-update',
            className: 'update-toast',
            description: 'Reload to get the latest version.',
            duration: Infinity,
            action: { label: 'Reload', onClick: () => window.location.reload() },
        });
    }, []);

    // Check when the tab regains focus (the moment a returning user with an old
    // tab comes back) plus a gentle backstop interval while foregrounded.
    useEffect(() => {
        const onVisible = () => check();
        document.addEventListener('visibilitychange', onVisible);
        const interval = setInterval(check, 30 * 60 * 1000);
        return () => {
            document.removeEventListener('visibilitychange', onVisible);
            clearInterval(interval);
        };
    }, [check]);

    // Re-check on navigation — e.g. finishing a session and landing on a safe page.
    //
    // Leaving the app for a public page also takes the prompt with it. The toast
    // is deliberately sticky (duration: Infinity) so it cannot be missed, which
    // also meant it followed the user out onto the landing page, where "a new
    // version is available" reads as something being wrong with the site rather
    // than a useful nudge. Raising it there was already prevented; dismissing it
    // was not.
    useEffect(() => {
        if (isPublicPath(pathname)) {
            toast.dismiss('app-update');
            // Free to prompt again once they are back inside the app: they still
            // have not reloaded, so the bundle they are running is still stale.
            promptedRef.current = false;
            return;
        }
        check();
    }, [pathname, check]);
};
